import { useMemo } from "react";

import { api } from "api/generated";

import { useAppDispatch } from "store/hooks";
import type { AppDispatch } from "store/store";

import { CALENDAR_DATA_MAX_AGE_S } from "utils/calendarRange";
import { preloadCalendarRoute } from "utils/calendarRoute";

import { useCalendarRange } from "./useCalendarRange";

/**
 * Stáhne data kalendáře dopředu, pokud nejsou mladší než CALENDAR_DATA_MAX_AGE_S. Argumenty musí být
 * z `getCalendarRange`, jinak se netrefí cache klíč stránky. [TD-476]
 */
export const prefetchCalendarData = (dispatch: AppDispatch, range: { dateFrom: string; dateTo: string }) => {
  dispatch(api.util.prefetch("getTimetable", range, { ifOlderThan: CALENDAR_DATA_MAX_AGE_S }));
};

/**
 * Obsluhy pro odkaz na /kalendar.
 *
 * AIDEV-NOTE: Dvě úrovně, protože widget řady a menu jsou vidět pořád. `preloadChunk` (levné, jednorázové)
 * patří na `onMouseEnter`; `prefetchCalendar` (chunk + data celé sezóny, znovu po 30 s) jen na záměr
 * otevřít stránku, tedy `onPointerDown` (dotyk, stisk myši) a `onFocus` (klávesnice). Při `onMouseEnter` by
 * každé přejetí kurzoru po hlavičce stahovalo data nadarmo. Data jen u načteného studenta — bez něj by
 * rozsah vycházel z náhradních hodnot a prefetch by vytvořil cache klíč, který stránka nepoužije. [TD-476]
 */
export const usePrefetchCalendar = () => {
  const dispatch = useAppDispatch();
  const { dateFrom, dateTo, isUserLoaded } = useCalendarRange();

  return useMemo(() => {
    const preloadChunk = () => {
      preloadCalendarRoute().catch(() => undefined);
    };

    const prefetchCalendar = () => {
      preloadChunk();
      if (isUserLoaded) prefetchCalendarData(dispatch, { dateFrom, dateTo });
    };

    return { preloadChunk, prefetchCalendar };
  }, [dispatch, dateFrom, dateTo, isUserLoaded]);
};
